import { bindEvents, getRootOffsetBounds, multiAddEventListener, multiRemoveEventListener, lodash } from '../../../assets/js/helpers';

import AnimationController from '../../../assets/js/controller/animation';
import CursorController from '../../../assets/js/controller/cursor';
import ResizeController from '../../../assets/js/controller/resize';
import ScrollController from '../../../assets/js/controller/scroll';

const selector = 'routing-experience';

export default class RoutingExperience {

	constructor(section) {
		this.el = {
			section,

			images: section.querySelectorAll(`.${selector}__image`),
			imagesInnerWrappers: section.querySelectorAll(`.${selector}__images-inner-wrapper`),
			pagination: section.querySelector(`.${selector}__counter-pagination`),
			textWrapper: section.querySelector(`.${selector}__text`),

			counterWrapper: section.querySelector(`.${selector}__counter > span`),

			headingWrappers: section.querySelectorAll(`.${selector}__text-heading > h2 > span`),
			metaWrappers: section.querySelectorAll(`.${selector}__meta > li`),

			textCtas: section.querySelectorAll(`.${selector}__cta`),
			allCtas: section.querySelectorAll('a'),

			cursor: section.querySelector(`.${selector}__cursor-expand`),

			headings: []
		};

		// class variable groups
		this.bounds = {
			imagesInnerWrappers: []
		};

		this.config = {
			interactionDelay: 800
		};

		this.controllers = {
			animationIndex: null,
			animationCursor: null,
			resize: null
		};

		this.debounced = {
			renderIndex: lodash.debounce(this.renderIndexNow.bind(this), 100, { leading: true })
		};

		this.flags = {
			isCursorActive: false,
			isInteractionReady: false
		};

		this.states = {
			hoverIndex: -1,
			index: -1,
			nextIndex: -1
		};

		this.timeouts = {
			init: null,
			pagination: null
		};

		this.throttled = {
			resize: lodash.throttle(this.measure.bind(this), 500, { leading: false, trailing: true })
		};

		// class variables

		// bind events to class scope
		this.events = bindEvents(this);

		// mosey
		this.init();
	}

	static get selector() {
		return selector;
	}

	// initialize

	init() {
		this.el.section.classList.add('is-init-setup');

		this.gatherElements();
		this.addEventListeners();
		this.renderIndex();

		AnimationController.one({
			update: () => true,
			render: () => {
				this.el.section.classList.remove('is-init-setup');
			}
		});

		this.timeouts.init = setTimeout(() => {
			this.flags.isInteractionReady = true;
			this.checkCursor();
		}, this.config.interactionDelay);
	}

	// prepare counter and heading variable collections

	gatherElements() {
		for (let i = 0; i < this.el.headingWrappers.length; i++) {
			const $headingWrapper = this.el.headingWrappers[i];
			const headingChildren = $headingWrapper.querySelectorAll('span');
			this.el.headings.push(headingChildren);
		}
	}

	// event listeners

	addEventListeners() {
		this.controllers.animationCursor = AnimationController.set({
			update: this.updateCursor.bind(this),
			render: this.renderCursor.bind(this)
		});

		this.controllers.animationIndex = AnimationController.set({
			update: this.updateIndex.bind(this),
			render: this.renderIndex.bind(this)
		});

		this.controllers.resize = ResizeController.set({
			update: this.events._onWindowResize,
			resize: () => null
		});

		multiAddEventListener(this.el.allCtas, 'mouseenter', this.events._onCtaEnter);
		multiAddEventListener(this.el.allCtas, 'mouseleave', this.events._onCtaLeave);

		this.measure(this.el.imagesInnerWrappers);
	}

	removeEventListeners() {
		AnimationController.clear(this.controllers.animationCursor);
		AnimationController.clear(this.controllers.animationIndex);
		ResizeController.clear(this.controllers.resize);

		multiRemoveEventListener(this.el.allCtas, 'mouseenter', this.events._onCtaEnter);
		multiRemoveEventListener(this.el.allCtas, 'mouseleave', this.events._onCtaLeave);

		clearTimeout(this.timeouts.init);
		clearTimeout(this.timeouts.pagination);
	}

	// listener methods

	_onWindowResize() {
		this.throttled.resize();
	}

	_onCtaEnter() {
		this.flags.isCursorOver = true;
		this.checkCursor();
	}

	_onCtaLeave() {
		this.flags.isCursorOver = false;
		this.checkCursor();
	}

	// cursor / cta

	checkCursor() {
		if (this.flags.isInteractionReady && this.flags.isCursorOver) {
			this.activateCursor();
		}
	}

	activateCursor() {
		if (!this.flags.isCursorActive && this.states.hoverIndex >= 0 && this.flags.isInteractionReady) {
			this.flags.isCursorActive = true;
			const $cursor = this.el.cursor;
			const $imagesInnerWrapper = this.el.imagesInnerWrappers[this.states.hoverIndex];
			const { formattedIndex } = $imagesInnerWrapper.dataset;
			const cursorText = $imagesInnerWrapper.dataset.cursor;
			$cursor.querySelector('span').innerHTML = cursorText.split('#').join(formattedIndex);

			this.el.cursor.classList.add('is-active');
			this.el.section.classList.add('is-cursor-active');
		}
	}

	deactivateCursor() {
		if (this.flags.isCursorActive) {
			this.flags.isCursorActive = false;
			this.el.cursor.classList.remove('is-active');
			this.el.section.classList.remove('is-cursor-active');
		}
	}

	updateCursor() {
		const { scrollX, scrollY } = CursorController.coords;
		let isIn = false;
		for (let i = 0; i < this.bounds.imagesInnerWrappers.length; i++) {
			const curImageWrapperBounds = this.bounds.imagesInnerWrappers[i];
			if (scrollX > curImageWrapperBounds.left && scrollX < curImageWrapperBounds.right
				&& scrollY > curImageWrapperBounds.top && scrollY < curImageWrapperBounds.bottom) {
				this.states.hoverIndex = i;
				isIn = true;
			}
		}

		if ((isIn && !this.flags.isCursorActive) || (!isIn && this.flags.isCursorActive)) {
			return true;
		}
	}

	renderCursor() {
		// swap cursor state based on position
		if (this.flags.isCursorActive) {
			this.deactivateCursor();
		} else {
			this.activateCursor();
		}
	}

	// animation controller events

	updateIndex() {
		const index = this.measureIndex();
		if (index !== this.states.nextIndex) {
			this.states.nextIndex = index;
			return true;
		}
	}

	renderIndex() {
		this.debounced.renderIndex();
	}

	renderIndexNow() {
		const index = this.measureIndex();
		const imagesInnerWrapper = this.el.imagesInnerWrappers[index];

		if (index >= 0 && index !== this.states.index) {
			const { link } = imagesInnerWrapper.dataset;
			const lastIndex = this.states.index;
			this.states.index = index;

			// initialize counter numbers

			const counterNumbers = this.el.counterWrapper.querySelectorAll('span');
			const y = (counterNumbers.length - 1 - this.states.index) * (-100);

			for (let i = 0; i < counterNumbers.length; i++) {
				const $number = counterNumbers[i];
				$number.style.transform = `translateY(${y}%)`;
			}

			// initialize headings

			for (let i = 0; i < this.el.headings.length; i++) {
				const headingChildren = this.el.headings[i];
				for (let k = 0; k < headingChildren.length; k++) {
					const $headingChild = headingChildren[k];
					const headingY = (headingChildren.length - 1 - this.states.index) * (-100);
					$headingChild.style.transform = `translateY(${headingY}%)`;
				}
			}

			// initialize cta's

			for (let i = 0; i < this.el.textCtas.length; i++) {
				const $cta = this.el.textCtas[i];
				$cta.href = link;
			}

			//

			clearTimeout(this.timeouts.pagination);

			if (index > lastIndex) {
				this.el.pagination.classList.add('is-out-down');
				this.el.metaWrappers[index].classList.add('is-init-down');
				if (lastIndex >= 0) {
					this.el.metaWrappers[lastIndex].classList.add('is-out-down');
				}
			} else {
				this.el.pagination.classList.add('is-out-up');
				this.el.metaWrappers[index].classList.add('is-init-up');
				if (lastIndex >= 0) {
					this.el.metaWrappers[lastIndex].classList.add('is-out-up');
				}
			}

			this.el.pagination.classList.remove('is-in');

			this.timeouts.pagination = setTimeout(() => {
				const $paginationSpan = this.el.pagination.querySelector('span');
				$paginationSpan.innerHTML = `(${this.el.pagination.dataset.total - this.states.index} / ${this.el.pagination.dataset.total})`;

				if (index > lastIndex) {
					this.el.pagination.classList.add('is-init-down');
				} else {
					this.el.pagination.classList.add('is-init-up');
				}

				AnimationController.one({
					update: () => true,
					render: () => {
						this.el.pagination.classList.remove('is-init-down', 'is-init-up', 'is-out-down', 'is-out-up');
						this.el.pagination.classList.add('is-in');

						this.el.metaWrappers[index].classList.remove('is-init-down', 'is-init-up');
						this.el.metaWrappers[index].classList.add('is-in');

						if (lastIndex >= 0) {
							this.el.metaWrappers[lastIndex].classList.remove('is-out-down', 'is-out-up', 'is-init-down', 'is-init-up', 'is-in');
						}
					}
				});

			}, 600);
		}
	}

	measure() {
		this.measureImages();
		this.measureIndex();
	}

	measureImages() {
		for (let i = 0; i < this.el.imagesInnerWrappers.length; i++) {
			const imagesInnerWrapper = this.el.imagesInnerWrappers[i];
			this.bounds.imagesInnerWrappers[i] = getRootOffsetBounds(imagesInnerWrapper);
		}
	}

	measureIndex() {
		let { index } = this.states;
		let { position } = ScrollController;

		position += window.innerHeight / 2;

		for (let i = 0; i < this.bounds.imagesInnerWrappers.length; i++) {
			const img = this.bounds.imagesInnerWrappers[i];
			const { top, bottom } = img;

			if (position > top && position < bottom) {
				index = i;
			}
		}

		return index;
	}

	// destroy

	destroy() {
		this.removeEventListeners();
	}

}
